/**
 * Registro tipado de las **integraciones con terceros** y de sus credenciales.
 *
 * Decisión de este repo (pedido de Damián, 3-sep-2026 — `docs/decisiones/secretos.md`):
 * **ninguna credencial de un tercero vive en un archivo**. El `.env` queda sólo para
 * infraestructura (`MONGODB_URI`, `MASTER_KEY`, JWT, puerto, zona, logs, límites,
 * `STORAGE_DRIVER`); todo lo demás se carga desde el portal y vive **cifrado en la base**
 * (`integrationCredentials`, `lib/crypto.ts`). Es el mismo patrón del hub de Deepyze y lo
 * que el AF ya pedía para ARCA: «el certificado fiscal y el CUIT se configuran una sola
 * vez, **desde el portal admin**» (RN-CON-01).
 *
 * Este archivo es el contrato compartido entre backend y SPA: la pantalla de Integraciones
 * se dibuja **desde acá** (labels, tipos de campo, ayudas, qué es secreto), y el backend
 * valida contra lo mismo. Agregar una integración es agregar una entrada, no tocar dos
 * repos.
 *
 * Qué declara cada integración:
 * - `rule`: la regla del AF que la justifica. Una integración sin regla no va.
 * - `fields`: cada campo con su `label`, `type`, `secret`, `required`, `help` (qué es y de
 *   dónde lo saca el cliente) y `placeholder`.
 * - `environments`: `null` cuando la integración no distingue ambiente.
 * - `testable` + `testDescription`: qué hace exactamente el botón «Probar».
 * - `fallback`: **el plan B cuando no está configurada.** Ninguna integración sin cargar
 *   puede romper el sistema: el club sigue cobrando, facturando y abriendo la puerta.
 */
export const INTEGRATION_ENVIRONMENTS = ["sandbox", "homologacion", "produccion"];
const field = (f) => f;
// ─────────────────────────────────────────────────────────────────────────────
export const INTEGRATIONS_REGISTRY = {
    "payway.terminal": {
        title: "Payway — Terminal integrada (posnet)",
        description: "El cobro presencial con tarjeta nace en el sistema y viaja a la terminal: nadie " +
            "tipea un importe en el posnet. Los números de cada terminal se cargan en " +
            "Configuración de caja → Terminales; acá van sólo las claves del comercio.",
        rule: "RN-CAJ-16",
        environments: ["sandbox", "homologacion", "produccion"],
        defaultEnvironment: "sandbox",
        testable: true,
        testDescription: "Pide un token de acceso a Payway con la clave pública y la secreta, en el ambiente " +
            "elegido. No crea ningún pago ni deja rastro en la caja.",
        fallback: "Cobro manual con cupón físico (lo autoriza un encargado o superior, con motivo), " +
            "pendiente de conciliar contra el lote. El club nunca deja de cobrar con tarjeta.",
        fields: [
            field({
                key: "baseUrl",
                label: "URL base de la API",
                type: "url",
                secret: false,
                required: false,
                help: "Dejala vacía: se usa la de Payway para el ambiente elegido (sandbox, homologación " +
                    "o producción). Sólo se completa si Payway indica otra.",
                placeholder: "https://api-sandbox.payway.com.ar",
            }),
            field({
                key: "publicKey",
                label: "Clave pública (client id)",
                type: "text",
                secret: false,
                required: true,
                help: "La clave pública del comercio para «Terminal integrada vía API». La entrega Payway " +
                    "junto con la secreta, una por ambiente.",
            }),
            field({
                key: "secretKey",
                label: "Clave secreta",
                type: "password",
                secret: true,
                required: true,
                maskTail: true,
                help: "La clave secreta que acompaña a la pública. Se guarda cifrada y no se vuelve a mostrar.",
            }),
            field({
                key: "cuit",
                label: "CUIT del comercio",
                type: "text",
                secret: false,
                required: true,
                help: "El CUIT con el que el club está dado de alta en Payway, sólo los 11 números.",
                placeholder: "30717170055",
                pattern: "^\\d{11}$",
                patternHint: "Son 11 números, sin guiones ni espacios.",
            }),
            field({
                key: "subnetAcquirerId",
                label: "Subred del adquirente (opcional)",
                type: "text",
                secret: false,
                required: false,
                help: "Dejalo vacío: sale del ambiente (sandbox 1, homologación 9, producción 2). Sólo se " +
                    "completa si Payway indica otro valor.",
                pattern: "^\\d{1,10}$",
                patternHint: "Sólo números.",
            }),
        ],
    },
    "payway.recurring": {
        title: "Payway — Débito recurrente tokenizado",
        description: "El motor de débitos: cada cuota de un socio adherido se cobra el día de su " +
            "vencimiento con la tarjeta guardada en la adhesión (el PAN jamás toca el sistema), " +
            "con reintentos y conciliación socio por socio. En modo «API» cobra con las claves " +
            "de «Payway — Pagos online (gateway)»: acá no se cargan claves propias.",
        rule: "RN-PLA-23",
        environments: ["homologacion", "produccion"],
        defaultEnvironment: "homologacion",
        testable: true,
        testDescription: "Con modo «API» prueba las claves de Payway — Pagos online (lista un pago con la " +
            "clave privada, sin cobrar nada) y avisa si esa integración no está cargada. Con " +
            "«Archivo / portal» verifica que la carpeta de presentaciones se pueda escribir.",
        fallback: "Presentación por archivo/portal, el esquema que el club ya opera hoy con Payway: " +
            "el sistema arma la presentación del día y la respuesta se carga para conciliar. " +
            "Se pierde el disparo automático, no la trazabilidad.",
        fields: [
            field({
                key: "driver",
                label: "Modo de presentación",
                type: "select",
                secret: false,
                required: true,
                options: [
                    { value: "mock", label: "Simulado (desarrollo)" },
                    { value: "file_batch", label: "Archivo / portal (plan B)" },
                    { value: "api", label: "API — cobro día a día con la tarjeta guardada" },
                ],
                help: "«API» cobra cada cuota en su vencimiento por Payway Pagos online, con las claves " +
                    "cargadas en «Payway — Pagos online (gateway)» (el mismo comercio con el que se " +
                    "guardó la tarjeta en la adhesión): no pide claves propias. Necesita que Payway " +
                    "habilite el cobro con tarjeta guardada sin código de seguridad. «Archivo / " +
                    "portal» es el esquema de presentación por lote que el club ya usa.",
            }),
            field({
                key: "merchantId",
                label: "Número de comercio",
                type: "text",
                secret: false,
                required: false,
                help: "Número de comercio que encabeza la presentación por archivo. Sólo con «Archivo / portal».",
            }),
            field({
                key: "batchDir",
                label: "Carpeta de presentaciones",
                type: "text",
                secret: false,
                required: false,
                help: "Ruta del servidor donde se dejan los archivos de presentación y desde donde se " +
                    "leen las respuestas. Sólo con «Archivo / portal».",
                placeholder: "./data/payway",
            }),
        ],
    },
    "payway.checkout": {
        title: "Payway — Pagos online (gateway)",
        description: "El cobro online desde la app (Link Checkout: la página de pago de Payway) y el débito " +
            "con tarjeta guardada (Payway Pagos online, ex Decidir). La tarjeta se carga en Payway " +
            "o se tokeniza en el navegador con la clave pública, y el sistema cobra y verifica con " +
            "la privada: el PAN no entra al sistema.",
        rule: "RN-PLA-31",
        environments: ["sandbox", "produccion"],
        defaultEnvironment: "sandbox",
        testable: true,
        testDescription: "Lista un pago (`GET /api/v2/payments?pageSize=1`) con la clave privada en el ambiente " +
            "elegido. Valida la clave privada; no cobra ni tokeniza nada.",
        fallback: "El socio paga en recepción (efectivo, posnet o transferencia) o adhiere al débito " +
            "de forma asistida en el mostrador. La app muestra la deuda igual.",
        fields: [
            field({
                key: "baseUrl",
                label: "URL base (opcional)",
                type: "url",
                secret: false,
                required: false,
                help: "Vacío = la oficial del ambiente: sandbox `https://developers-ventasonline.payway.com.ar`, " +
                    "producción `https://ventasonline.payway.com.ar`. Sólo se carga si Payway indica otra.",
                placeholder: "https://ventasonline.payway.com.ar",
            }),
            field({
                key: "publicKey",
                label: "Clave pública",
                type: "text",
                secret: false,
                required: true,
                help: "Viaja al navegador o a la app del socio para tokenizar la tarjeta: es pública por " +
                    "definición. La entrega Payway con el alta del comercio en Pagos online.",
            }),
            field({
                key: "privateKey",
                label: "Clave privada",
                type: "password",
                secret: true,
                required: true,
                maskTail: true,
                help: "La usa sólo el sistema para cobrar, consultar y devolver. Nunca sale de la base. " +
                    "La entrega Payway junto con la pública, una por ambiente.",
            }),
            field({
                key: "siteId",
                label: "Site ID (opcional)",
                type: "text",
                secret: false,
                required: false,
                help: "Número de comercio en Payway. Sólo si Payway lo pide para comercios agrupados.",
            }),
            field({
                key: "internalTokenization",
                label: "Tokenización interna habilitada por Payway",
                type: "select",
                secret: false,
                required: false,
                options: [
                    { value: "false", label: "No (todavía no la habilitó)" },
                    { value: "true", label: "Sí, Payway la habilitó para el comercio" },
                ],
                help: "Permite crear el token de una tarjeta a partir de su número, sin cobrar nada. Es lo " +
                    "que pasa a los adheridos al débito por archivo (PrismaNet) al débito día a día sin " +
                    "pedirles la tarjeta de nuevo. Se marca «Sí» recién cuando Payway confirma por " +
                    "escrito que la habilitó: mientras diga «No», la acción «Pasar adheridos a débito " +
                    "día a día» queda deshabilitada.",
            }),
            field({
                key: "establishmentName",
                label: "Nombre en el resumen (opcional)",
                type: "text",
                secret: false,
                required: false,
                help: "Cómo figura el cobro en el resumen de la tarjeta del socio.",
                placeholder: "VIVA TRAINING CLUB",
            }),
            field({
                key: "checkoutBaseUrl",
                label: "URL del Link Checkout (opcional)",
                type: "url",
                secret: false,
                required: false,
                help: "La página de pago de Payway con la que el socio renueva o paga una cuota desde la " +
                    "app. Vacío = la oficial del ambiente: sandbox " +
                    "`https://developers.decidir.com/api/v1/checkout-payment-button`, producción " +
                    "`https://ventasonline.payway.com.ar/api/v1/checkout-payment-button`. Sólo se carga " +
                    "si Payway indica otra.",
                placeholder: "https://ventasonline.payway.com.ar/api/v1/checkout-payment-button",
            }),
            field({
                key: "checkoutSite",
                label: "Número de comercio del Link Checkout",
                type: "text",
                secret: false,
                // No se exige al guardar: el débito con tarjeta guardada funciona sin él. El cobro
                // desde la app SÍ lo exige (el Link Checkout manda `site`): sin éste ni un Site ID,
                // la app no ofrece pagar online (`paywayCheckoutAvailability`).
                required: false,
                help: "El número de comercio (site) que Payway asignó para la página de pago. Sin él, el " +
                    "socio no puede pagar desde la app y la app lo manda a recepción. Vacío = se usa el " +
                    "Site ID de arriba, si está cargado.",
                placeholder: "00097001",
                pattern: "^\\d{1,20}$",
                patternHint: "Sólo números.",
            }),
            field({
                key: "establishmentNumber",
                label: "Número de establecimiento (opcional)",
                type: "text",
                secret: false,
                required: false,
                help: "Sólo si Payway asignó un número de establecimiento para el Link Checkout. Son " +
                    "sólo dígitos; figura en el alta del comercio.",
            }),
        ],
    },
    arca: {
        title: "ARCA — Facturación electrónica (WSAA + WSFEv1)",
        description: "Factura A/B y notas de crédito/débito con CAE en línea y PDF con QR. Emisor: el " +
            "CUIT de Agua Viva Recreación y Deporte SRL, uno solo para las tres sedes.",
        rule: "RN-CON-01",
        environments: ["homologacion", "produccion"],
        defaultEnvironment: "homologacion",
        testable: true,
        testDescription: "Pide un Ticket de Acceso al WSAA del ambiente configurado firmando con el " +
            "certificado cargado. Es la prueba real de que el certificado está asociado al " +
            "servicio `wsfe`. No emite ningún comprobante.",
        fallback: "Sin certificado cargado, los comprobantes quedan «pendientes de CAE» con su numeración " +
            "por punto de venta y se reintentan solos. ARCA nunca bloquea un cobro ni " +
            "un cierre de caja.",
        fields: [
            field({
                key: "cuit",
                label: "CUIT del emisor",
                type: "text",
                secret: false,
                required: true,
                help: "CUIT de la SRL, sin guiones. Es el mismo para las tres sedes; el punto de venta " +
                    "por recepción se carga en cada sede (Estructura), no acá.",
                placeholder: "30712345678",
            }),
            field({
                key: "certPem",
                label: "Certificado X.509 (PEM)",
                type: "file",
                secret: true,
                required: true,
                accept: [".pem", ".crt", ".cer", ".p12", ".pfx"],
                help: "El certificado que ARCA emite para el CUIT. El cliente (o su contador) lo genera " +
                    "con clave fiscal en «Administración de Certificados Digitales» y lo asocia al " +
                    "servicio `wsfe`. Se sube acá: no va a ningún disco ni a S3.",
            }),
            field({
                key: "keyPem",
                label: "Clave privada (PEM)",
                type: "file",
                secret: true,
                required: true,
                accept: [".pem", ".key"],
                help: "La clave privada con la que se generó el CSR del certificado. Es el secreto más " +
                    "sensible del sistema: es la capacidad de facturar. Se guarda cifrada y no se " +
                    "muestra jamás.",
            }),
        ],
    },
    interbanking: {
        title: "Interbanking — Feed de movimientos bancarios",
        description: "Alimenta la solapa Transferencias de Conciliación con los créditos de las cuentas " +
            "de la SRL (Francés e ICBC). Un feed caído no es un feed vacío.",
        rule: "RN-CAJ-19",
        environments: null,
        defaultEnvironment: null,
        testable: true,
        testDescription: "Pide el feed del día para la primera cuenta bancaria cargada. Devuelve cuántos " +
            "movimientos vio; si el feed no contesta, lo dice — nunca reporta cero movimientos.",
        fallback: "Importación manual del extracto CSV/Excel que el banco ya le entrega al contador, " +
            "a la MISMA pantalla y con el mismo matcher. Menos frecuencia, misma disciplina.",
        fields: [
            field({
                key: "baseUrl",
                label: "URL base del servicio",
                type: "url",
                secret: false,
                required: true,
                help: "Endpoint del plan contratado con Interbanking (Datanet / consulta de movimientos).",
            }),
            field({
                key: "user",
                label: "Usuario",
                type: "text",
                secret: false,
                required: true,
                help: "Usuario del servicio, dado de alta por los apoderados de la SRL en Interbanking.",
            }),
            field({
                key: "password",
                label: "Contraseña",
                type: "password",
                secret: true,
                required: true,
                help: "Contraseña del usuario del servicio. Se guarda cifrada y no se puede volver a leer.",
            }),
            field({
                key: "apiKey",
                label: "API key / token",
                type: "password",
                secret: true,
                required: false,
                maskTail: true,
                help: "Cuando el plan usa token en vez de usuario y contraseña. Lo entrega Interbanking.",
            }),
        ],
    },
    "mercadopago.point": {
        title: "Mercado Pago — Point y QR del kiosko",
        description: "Cobro del kiosko y autopago de cuota en la tablet. MP queda SÓLO para el kiosko: " +
            "los recurrentes van por Payway. La cuenta tiene que ser la de la SRL.",
        rule: "RN-CON-25",
        environments: null,
        defaultEnvironment: null,
        testable: true,
        testDescription: "Consulta el dispositivo Point configurado contra la API de MP con el access token " +
            "cargado. No manda ningún importe al dispositivo.",
        fallback: "El kiosko cobra en efectivo (a la caja del turno) o con posnet Payway desde " +
            "recepción. La venta se registra igual.",
        fields: [
            field({
                key: "accessToken",
                label: "Access token",
                type: "password",
                secret: true,
                required: true,
                maskTail: true,
                help: "Token de producción de la cuenta MP **de la SRL** (Tus integraciones → " +
                    "Credenciales). La cuenta personal no puede ser destino.",
            }),
            field({
                key: "deviceId",
                label: "ID del dispositivo Point",
                type: "text",
                secret: false,
                required: false,
                help: "Id del Point del kiosko. Se lee desde la app de MP o del listado de dispositivos.",
            }),
            field({
                key: "userId",
                label: "User ID de la cuenta",
                type: "text",
                secret: false,
                required: false,
                help: "Id de la cuenta MP de la SRL. Sirve para verificar que el feed es de la cuenta correcta.",
            }),
            field({
                key: "webhookSecret",
                label: "Secreto del webhook",
                type: "password",
                secret: true,
                required: false,
                help: "Firma de las notificaciones de MP. Sin él, el resultado se resuelve por polling.",
            }),
        ],
    },
    gsd: {
        title: "Molinetes GSD — Controladora y agente de acceso",
        description: "El sistema es la única autoridad de apertura: recibe la identificación, corre las " +
            "validaciones y decide abrir. El agente local habla con la controladora.",
        rule: "RN-ACC-02 / PV-36",
        environments: null,
        defaultEnvironment: null,
        testable: true,
        testDescription: "Hace un ping HTTP al agente de acceso del local configurado y espera su heartbeat. " +
            "No abre ningún molinete.",
        fallback: "Lectora QR + apertura por relé, y la huella sigue en el sistema actual (plan B). " +
            "Si ni el relé está accesible, recepción habilita a mano con el " +
            "semáforo en pantalla: todo ingreso queda registrado igual.",
        fields: [
            field({
                key: "controllerModel",
                label: "Modelo de controladora",
                type: "text",
                secret: false,
                required: true,
                help: "Modelo exacto de la controladora instalada en la sede. Sale de la placa de " +
                    "la controladora.",
                placeholder: "GSD ...",
            }),
            field({
                key: "protocol",
                label: "Protocolo de apertura",
                type: "select",
                secret: false,
                required: true,
                options: [
                    { value: "relay", label: "Relé seco" },
                    { value: "wiegand", label: "Wiegand" },
                    { value: "rs485", label: "RS-485" },
                    { value: "tcp_sdk", label: "TCP con SDK" },
                ],
                help: "Interfaz por la que el agente ordena la apertura. Depende del modelo de controladora.",
            }),
            field({
                key: "agentBaseUrl",
                label: "URL del agente de acceso",
                type: "url",
                secret: false,
                required: true,
                help: "Host y puerto del agente local del punto de acceso, uno por local. " +
                    "Ej.: `http://10.0.0.20:4810`.",
                placeholder: "http://10.0.0.20:4810",
            }),
            field({
                key: "agentApiKey",
                label: "API key del agente",
                type: "password",
                secret: true,
                required: true,
                maskTail: true,
                help: "La misma API key revocable con la que el agente se identifica ante el sistema. " +
                    "Se genera en Puntos de acceso y se copia acá.",
            }),
        ],
    },
    zkteco: {
        title: "Molinete ZKTeco — terminal de huella",
        description: "El terminal ZKTeco del molinete sigue reconociendo la huella y abriendo (anda sin " +
            "internet). El sistema le carga el padrón —alta con el número de socio, bloqueo y " +
            "desbloqueo según la cuota, el apto y el plan—, recibe cada ingreso y puede abrir " +
            "desde recepción. Los equipos se cargan en Configuración → Dispositivos de acceso → " +
            "ZKTeco; acá va sólo la dirección pública que se le configura al equipo.",
        rule: "RN-ACC-02 / RN-ACC-05",
        environments: null,
        defaultEnvironment: null,
        testable: true,
        testDescription: "Repasa los equipos cargados: cuándo habló cada uno por última vez, si tiene backup " +
            "y cuántos comandos tiene con error. No le manda nada al equipo ni abre el molinete.",
        fallback: "El terminal sigue abriendo con las huellas que ya tiene, como hasta hoy. Sin " +
            "sincronización, recepción ve los ingresos y habilita a mano desde el semáforo.",
        fields: [
            field({
                key: "serverHost",
                label: "Dirección del servidor para el equipo",
                type: "text",
                secret: false,
                required: false,
                help: "La que se carga en el menú «Servidor en la nube / ADMS» del terminal. Dejala " +
                    "vacía para usar la del sistema (apigimnasio.sistemaviva.com.ar, puerto 80).",
                placeholder: "apigimnasio.sistemaviva.com.ar",
            }),
        ],
        deviceFields: [
            field({
                key: "name",
                label: "Nombre del equipo",
                type: "text",
                secret: false,
                required: true,
                help: "Cómo lo va a reconocer recepción. Ej.: «Molinete Olivos».",
                placeholder: "Molinete Olivos",
            }),
            field({
                key: "channel",
                label: "Cómo se conecta",
                type: "select",
                secret: false,
                required: true,
                options: [
                    { value: "push", label: "Por la nube (ADMS): el equipo llama al sistema" },
                    { value: "agent", label: "Desde la PC del gimnasio (viva-molinete.exe)" },
                ],
                help: "Si el menú del terminal tiene «Servidor en la nube» o «ADMS», usá la nube: no hay " +
                    "que instalar nada. Si no lo tiene, instalá viva-molinete.exe en la PC del gimnasio.",
            }),
            field({
                key: "serialNumber",
                label: "Número de serie",
                type: "text",
                secret: false,
                required: false,
                requiredWhen: { field: "channel", equals: "push" },
                help: "Está en Menú → Info del sistema → Info del dispositivo. Por la nube es lo que " +
                    "identifica al equipo: uno que no está cargado acá no recibe nada.",
            }),
            field({
                key: "ip",
                label: "IP del equipo en la red del gimnasio",
                type: "text",
                secret: false,
                required: false,
                requiredWhen: { field: "channel", equals: "agent" },
                help: "Menú → Comunicación → Red. Conviene dejarla fija (o reservarla en el módem) para " +
                    "que no cambie.",
                placeholder: "192.168.1.201",
            }),
            field({
                key: "port",
                label: "Puerto",
                type: "text",
                secret: false,
                required: false,
                help: "Casi siempre 4370.",
                placeholder: "4370",
                pattern: "^\\d{1,5}$",
                patternHint: "Sólo números.",
            }),
            field({
                key: "commKey",
                label: "Comm Key (clave de comunicación)",
                type: "password",
                secret: true,
                required: false,
                help: "Menú → Comunicación → Conexión de PC → Clave de comunicación. Vacía si el equipo " +
                    "no tiene. Se guarda cifrada.",
                pattern: "^\\d{0,9}$",
                patternHint: "Sólo números.",
            }),
            field({
                key: "accessPointId",
                label: "Punto de acceso (molinete)",
                type: "select",
                secret: false,
                required: true,
                help: "El molinete que abre este terminal. Define el local contra el que se valida.",
            }),
            field({
                key: "blockMode",
                label: "Cómo se bloquea a un socio",
                type: "select",
                secret: false,
                required: true,
                options: [
                    { value: "disable", label: "Deshabilitarlo en el equipo (conserva la huella)" },
                    { value: "delete", label: "Borrarlo del equipo y recargarlo al habilitarlo" },
                ],
                help: "Deshabilitar es lo más seguro. Borrar sirve si el equipo no respeta el usuario " +
                    "deshabilitado: el sistema lo vuelve a cargar con sus huellas al pagar.",
            }),
        ],
    },
    email: {
        title: "Email transaccional",
        description: "Canal operativo del sistema: avisos del catálogo, comprobantes, códigos de ingreso " +
            "(segundo factor del portal y acceso a la app) e invitación del socio a la app.",
        rule: "RN-CRM-10",
        environments: null,
        defaultEnvironment: null,
        testable: true,
        testDescription: "Manda un mail de prueba **a la casilla del usuario que aprieta Probar**. Con " +
            "proveedor SMTP, antes se conecta al servidor y valida usuario y contraseña: un " +
            "error de credenciales aparece acá, en el botón, y no en el primer código de " +
            "ingreso que nunca llega. Si el mail llega, el canal funciona de punta a punta.",
        fallback: "Sin casilla configurada el mail NO se manda, pero el aviso queda igual en el centro " +
            "de avisos de la app. Nada se pierde en silencio.",
        fields: [
            field({
                key: "provider",
                label: "Proveedor",
                type: "select",
                secret: false,
                required: true,
                options: [
                    { value: "ses", label: "Amazon SES" },
                    { value: "resend", label: "Resend" },
                    { value: "smtp", label: "SMTP — Gmail, Outlook, hosting" },
                ],
                help: "Amazon SES es el propuesto y Resend la alternativa: los dos hablan por HTTPS y " +
                    "aguantan un aviso a los 2.500 socios de golpe. «SMTP» manda con una casilla que " +
                    "ya existe (Gmail, Outlook, el hosting del club): sirve para arrancar sin " +
                    "contratar nada, con un techo — una cuenta de Gmail personal manda unos 500 " +
                    "destinatarios por día y Google Workspace unos 2.000. Alcanza para códigos de " +
                    "ingreso, comprobantes e invitaciones; NO para un aviso masivo a todos los " +
                    "socios. Para eso, SES o Resend.",
            }),
            field({
                key: "from",
                label: "Remitente",
                type: "text",
                secret: false,
                required: true,
                help: "Casilla desde la que sale todo. Con SES o Resend tiene que estar verificada en " +
                    "el proveedor, y el dominio con SPF/DKIM/DMARC. Con SMTP de Gmail tiene que ser " +
                    "LA MISMA dirección que el usuario de abajo (o un alias que hayas verificado en " +
                    "Gmail, en Configuración → Cuentas → «Enviar como»): cualquier otra, Gmail la " +
                    "reescribe por la de la cuenta sin avisar.",
                placeholder: "no-reply@vivatrainingclub.com.ar",
            }),
            field({
                key: "region",
                label: "Región (SES)",
                type: "text",
                secret: false,
                required: false,
                help: "Región de SES. Sólo con proveedor `ses`.",
                placeholder: "sa-east-1",
            }),
            field({
                key: "accessKeyId",
                label: "Access key ID (SES)",
                type: "text",
                secret: false,
                required: false,
                help: "Del usuario IAM con permiso `ses:SendEmail`. Si se deja vacío y el server corre " +
                    "en AWS, se usa el rol de la instancia.",
            }),
            field({
                key: "secretAccessKey",
                label: "Secret access key (SES)",
                type: "password",
                secret: true,
                required: false,
                help: "El secreto del usuario IAM. Se guarda cifrado.",
            }),
            field({
                key: "apiKey",
                label: "API key (Resend)",
                type: "password",
                secret: true,
                required: false,
                maskTail: true,
                help: "Clave de Resend. Sólo con proveedor `resend`.",
            }),
            /*
             * SMTP: la receta completa va en las ayudas porque el que la va a seguir es el dueño
             * del club, no un técnico, y la pantalla es lo único que va a tener adelante. Los
             * cuatro que hacen falta para conectar son obligatorios SÓLO con `provider: smtp`
             * (`requiredWhen`); con SES o Resend quedan vacíos y no molestan.
             */
            field({
                key: "smtpHost",
                label: "Servidor SMTP",
                type: "text",
                secret: false,
                required: false,
                requiredWhen: { field: "provider", equals: "smtp" },
                help: "Sólo con proveedor `smtp`. Para Gmail y Google Workspace es `smtp.gmail.com`; " +
                    "para Outlook / Microsoft 365, `smtp.office365.com` (ojo: Microsoft está " +
                    "retirando el ingreso por usuario y contraseña — desde fines de 2026 queda " +
                    "apagado por defecto y lo tiene que prender el administrador de la cuenta; si " +
                    "«Probar» falla con datos correctos, es eso); para el hosting del club, el que " +
                    "figura en su panel (suele ser `mail.` seguido del dominio).",
                placeholder: "smtp.gmail.com",
            }),
            field({
                key: "smtpPort",
                label: "Puerto (SMTP)",
                type: "text",
                secret: false,
                required: false,
                requiredWhen: { field: "provider", equals: "smtp" },
                pattern: "^[1-9]\\d{0,4}$",
                patternHint: "tiene que ser un número de puerto (Gmail: 465 o 587)",
                help: "Sólo con proveedor `smtp`. Con Gmail: 465 con «SSL/TLS» (o 587 con «STARTTLS», " +
                    "que es lo mismo por otro camino). El puerto y la seguridad van de a pares: si " +
                    "cambiás uno, cambiá el otro.",
                placeholder: "465",
            }),
            field({
                key: "smtpSecurity",
                label: "Seguridad (SMTP)",
                type: "select",
                secret: false,
                required: false,
                options: [
                    { value: "ssl", label: "SSL/TLS (465)" },
                    { value: "starttls", label: "STARTTLS (587)" },
                ],
                help: "Sólo con proveedor `smtp`. «SSL/TLS» cifra desde el primer byte y va con el " +
                    "puerto 465; «STARTTLS» arranca en claro y cifra enseguida, y va con el 587. " +
                    "Gmail acepta las dos; si dudás, SSL/TLS con 465. Vacío = SSL/TLS.",
            }),
            field({
                key: "smtpUser",
                label: "Usuario (SMTP)",
                type: "text",
                secret: false,
                required: false,
                requiredWhen: { field: "provider", equals: "smtp" },
                help: "Sólo con proveedor `smtp`. Es la dirección de mail COMPLETA de la casilla (con " +
                    "el @gmail.com o el dominio), no un nombre de usuario.",
                placeholder: "club@gmail.com",
            }),
            field({
                key: "smtpPass",
                label: "Contraseña (SMTP)",
                type: "password",
                secret: true,
                required: false,
                requiredWhen: { field: "provider", equals: "smtp" },
                maskTail: true,
                help: "Sólo con proveedor `smtp`. Con Gmail NO es la contraseña de la cuenta: es una " +
                    "«contraseña de aplicación» de 16 letras que se genera una sola vez en " +
                    "myaccount.google.com/apppasswords — hace falta tener la verificación en dos " +
                    "pasos activada, si no la opción ni aparece. Se pega acá tal cual (con o sin los " +
                    "espacios) y se guarda cifrada.",
            }),
        ],
    },
    push: {
        title: "Push (Expo)",
        description: "Notificaciones a la app de socios sobre APNs y FCM, en lotes de 100. Todo push " +
            "tiene su espejo en el centro de avisos.",
        rule: "RN-CRM-10",
        environments: null,
        defaultEnvironment: null,
        testable: true,
        testDescription: "Valida el access token contra la API de Expo. No manda ninguna notificación a " +
            "ningún socio.",
        fallback: "Sin token configurado el aviso no sale al celular, pero queda en el centro de avisos " +
            "de la app y el socio lo ve al abrirla.",
        fields: [
            field({
                key: "accessToken",
                label: "Expo access token",
                type: "password",
                secret: true,
                required: true,
                maskTail: true,
                help: "Token de la cuenta de Expo del proyecto (expo.dev → Access tokens). Sin él, " +
                    "Expo acepta el envío pero sin autenticar el proyecto.",
            }),
            field({
                key: "projectId",
                label: "Project ID de Expo",
                type: "text",
                secret: false,
                required: false,
                help: "Id del proyecto EAS. Sirve para verificar que el token es el de esta app.",
            }),
        ],
    },
    storage: {
        title: "Almacenamiento de archivos (S3)",
        description: "Aptos médicos, firmas, comprobantes, PDFs de ARCA y fotos, guardados fuera del " +
            "servidor con acceso firmado. Sin S3 configurado se guardan en el disco del servidor " +
            "y no hace falta cargar nada.",
        rule: "Arquitectura §10",
        environments: null,
        defaultEnvironment: null,
        testable: true,
        testDescription: "Escribe, lee y borra un objeto de prueba en el bucket. Es la única forma de saber " +
            "que los permisos alcanzan para las tres cosas.",
        fallback: "Sin S3 configurado, los archivos se guardan en el disco del servidor y se sirven " +
            "desde el propio sistema, sólo a quien tiene sesión.",
        fields: [
            field({
                key: "bucket",
                label: "Bucket",
                type: "text",
                secret: false,
                required: true,
                help: "Nombre del bucket S3 del proyecto. Tiene que existir y estar en la región de abajo.",
            }),
            field({
                key: "region",
                label: "Región",
                type: "text",
                secret: false,
                required: true,
                help: "Región del bucket.",
                placeholder: "sa-east-1",
            }),
            field({
                key: "accessKeyId",
                label: "Access key ID",
                type: "text",
                secret: false,
                required: false,
                help: "Del usuario IAM con acceso al bucket. Vacío ⇒ se usa el rol de la instancia EC2, " +
                    "que es lo recomendado.",
            }),
            field({
                key: "secretAccessKey",
                label: "Secret access key",
                type: "password",
                secret: true,
                required: false,
                help: "El secreto del usuario IAM. Se guarda cifrado.",
            }),
        ],
    },
};
export const INTEGRATION_KEYS = Object.keys(INTEGRATIONS_REGISTRY);
export function isIntegrationKey(key) {
    return Object.prototype.hasOwnProperty.call(INTEGRATIONS_REGISTRY, key);
}
export function getIntegrationDefinition(key) {
    return INTEGRATIONS_REGISTRY[key];
}
/** Campos de una integración. */
export function integrationFields(key) {
    return INTEGRATIONS_REGISTRY[key].fields;
}
export function integrationField(key, fieldKey) {
    return INTEGRATIONS_REGISTRY[key].fields.find((f) => f.key === fieldKey);
}
/** Los campos que se guardan cifrados y **nunca** salen por la API. */
export function secretFieldKeys(key) {
    return INTEGRATIONS_REGISTRY[key].fields.filter((f) => f.secret).map((f) => f.key);
}
/** Los campos sin los cuales la integración NO se puede usar, **sin mirar lo cargado**. */
export function requiredFieldKeys(key) {
    return INTEGRATIONS_REGISTRY[key].fields.filter((f) => f.required).map((f) => f.key);
}
/**
 * Los obligatorios **dados los valores cargados**: los incondicionales más los que
 * dependen de otro campo (`requiredWhen`). Con `provider: smtp` entran host, puerto,
 * usuario y contraseña; con `ses` no. Es lo que decide «configurada» vs «incompleta» y
 * lo que `getIntegration` exige para entregar la configuración a un cliente.
 */
export function requiredFieldKeysFor(key, values) {
    return INTEGRATIONS_REGISTRY[key].fields
        .filter((f) => f.required ||
        (f.requiredWhen !== undefined &&
            (values[f.requiredWhen.field] ?? "") === f.requiredWhen.equals))
        .map((f) => f.key);
}
/** Los que se suben como archivo (certificado y clave de ARCA). */
export function fileFieldKeys(key) {
    return INTEGRATIONS_REGISTRY[key].fields.filter((f) => f.type === "file").map((f) => f.key);
}
/**
 * Máscara de un secreto cargado. `maskTail` ⇒ `"••••1234"` (los últimos 4, que el cliente
 * puede cotejar contra el panel del proveedor); si no, `"cargado"` — de una clave privada
 * no se muestra ni un carácter. Un campo vacío devuelve `null`: «no está cargado» y
 * «está cargado pero no te lo muestro» son cosas distintas y se ven distintas.
 */
export function maskSecret(value, maskTail = false) {
    if (!value)
        return null;
    if (!maskTail || value.length < 8)
        return "cargado";
    return `••••${value.slice(-4)}`;
}
//# sourceMappingURL=integrations-registry.js.map